import clsx from "clsx";

import { getTrackingColor } from "~/modules/dashboard/components/TimeTrackingCalendar/entryStyles.ts";
import type { TimeTrackingExtendedType } from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import {
	getTimeTrackingBarTheme,
} from "~/modules/timeTracking/components/components/Day/components/TimeTrackingBar/getTimeTrackingBarTheme.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

type Props = {
	timeTracking: TimeTrackingExtendedType;
	size?: "sm";
	hideHours?: boolean;
	isOpen?: boolean;
	onClick?: () => void;
};

/**
 * A tracking as a quiet text row: a dot in the type color, the title and the hours.
 * Only `DayColumn` and the month grid use it. The day list shows its trackings as cards (`TrackingEntryRow`).
 * The time tracking page keeps `TimeTrackingBar` with its filled bars.
 * A row without `onClick` is plain, so a button can hold it.
 */
export function TrackingRow({ timeTracking, size, hideHours, isOpen, onClick }: Props) {
	const { theme, title } = getTimeTrackingBarTheme(timeTracking);
	const color = getTrackingColor(theme);
	const className = clsx(
		"flex w-full min-w-0 items-center gap-1.5 rounded px-1 text-left",
		size === "sm" ? "py-0.5 text-[11px] leading-4" : "py-2 text-sm",
		onClick && "hover:bg-gray-100",
		isOpen && "bg-gray-100",
	);
	const content = (
		<>
			<span className={clsx("size-1.5 shrink-0 rounded-full", color.solid)} />
			<span className={clsx("min-w-0 truncate font-bold", theme === "projectNotBillable" ? color.text : "text-gray-800")}>{title ?? "Erfassung"}</span>
			{!hideHours && <span className="ml-auto shrink-0 text-gray-400">{formatNumberWithComma(timeTracking.minutes / 60, 1, false)} h</span>}
		</>
	);

	return onClick ? (
		<button type="button"
				className={className}
				onClick={onClick}>{content}</button>
	) : (
		<div className={className}>{content}</div>
	);
}
